import React, { useEffect, useRef } from 'react'
import { DismissButton, usePopover } from 'react-aria'
import styled from 'styled-components'
import { SelectState } from 'react-stately'
import { SafeFocusScope } from '../../../internal-helpers/SafeFocusScope'
import { Box, Flex } from '../../../core-components'
import { useElementSize } from '../../hooks'

type Props = {
  children: React.ReactNode
  state: SelectState<unknown>
  triggerRef: React.RefObject<HTMLButtonElement | null>
  triggerWidth: number
}

export const BaseSelectPopover = ({ children, triggerWidth, state, triggerRef }: Props) => {
  const popoverRef = useRef<HTMLDivElement>(null)
  const containerPadding = 12
  const { popoverProps, underlayProps } = usePopover(
    {
      popoverRef,
      triggerRef,
      placement: 'bottom start',
      offset: 8,
      maxHeight: 496,
      containerPadding,
    },
    state
  ) // ariaHideOutside is implicitly part of usePopover

  useEffect(() => {
    setTimeout(() => {
      popoverRef.current?.querySelector('input')?.focus()
    }, 0)
  }, [popoverRef])

  const { height } = useElementSize(popoverRef)

  return (
    <SafeFocusScope restoreFocus contain>
      <Box {...underlayProps} position='fixed' top={0} right={0} bottom={0} left={0} />
      <StyledPopover
        minWidth={triggerWidth}
        maxWidth={`calc(100% - ${containerPadding * 2}px)`}
        ref={popoverRef}
        minHeight={height}
        borderRadius={8}
        boxShadow='border/popover'
        {...popoverProps}
      >
        <Flex
          flexDirection='column'
          borderRadius={8}
          overflow='hidden'
          maxHeight='inherit'
          flexGrow={1}
        >
          {children}
          <DismissButton onDismiss={() => state.close()} />
        </Flex>
      </StyledPopover>
    </SafeFocusScope>
  )
}

const StyledPopover = styled(Box)<{ minHeight: number; minWidth: number }>`
  display: flex;
  flex-direction: column;
  width: max-content;
  min-width: ${({ minWidth }) => `${minWidth}px`};
  min-height: ${({ minHeight }) => `${minHeight}px`};
`
